import React, { useEffect, useState } from 'react'
import { useShowContext, SimpleShowLayout, Show, useTranslate } from 'react-admin'
import { Grid, Container, Skeleton } from '@mui/material'
import { CardCommon } from 'src/components/uiElements'
import { ShowListElement } from 'src/components/uiElements/CRUD/ShowListElement'
import { CardCrudBottomButtons } from 'src/components/uiElements/CRUD/CardCrudBottomButtons'
import { businessRuleServices } from 'src/services/sales/businessRule/businessRule.service'
import { useQuery } from 'react-query'
import {
  AIR,
  HOTEL,
  TRAIN,
  PAYMENT_METHOD,
  CAR,
  LIQUIDATION,
  BILLING,
  OTHER,
  TRAIN_SEGMENT,
} from 'src/components/pages/sales/business-rule/helper/const'
import { CheckboxContext } from 'src/components/pages/sales/business-rule/logic/CheckboxContext'
import { FormulaComponent } from './logic/FormulaComponent'
import { isUndefined } from 'src/helper/isUndefined'
import {
  formatCheckedBox,
  updateCheckBox,
  waitVariablesFormat,
} from 'src/components/pages/sales/business-rule/helper/vars_helper'
import { mapperHelperService } from 'src/services/auth/mapperHelper.service'
import { operators } from 'src/components/pages/sales/business-rule/logic/ListOperators'
import { getSupplierByProduct } from 'src/components/pages/sales/reservation/helper/getSupplierByProduct'
import { productServices } from 'src/services/reservation/product/product.service'

const ShowWrapper = ({ children }) => <Container maxWidth='false'> {children}</Container>

const ShowContent = () => {
  const translate = useTranslate()
  const { record } = useShowContext()
  const [airSources, setAirSources] = useState([])
  const [airDestinations, setAirDestinations] = useState([])
  const [airAirlines, setAirAirlines] = useState([])
  const [hotelSuppliers, setHotelSuppliers] = useState([])
  const [hotelCountries, setHotelCountries] = useState([])
  const [hotelCities, setHotelCities] = useState([])
  const [hotelSources, setHotelSources] = useState([])
  const [OtherSuppliers, setOtherSuppliers] = useState([])
  const [OtherSuppliersLoading, setOtherSuppliersLoading] = useState(false)

  const [checkedBox, setCheckedBox] = useState([])
  const [variablesFormat, setVariablesFormat] = useState([])
  const [formulasLoading, setFormulasLoading] = useState(false)
  const [formulas, setFormulas] = useState([])

  const renter = useQuery('ext-car-renter', businessRuleServices.getRenterCar)

  useEffect(() => {
    let format_checkedBox = formatCheckedBox(checkedBox, operators, variablesFormat, formulas)
    setVariablesFormat(format_checkedBox)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkedBox])

  useEffect(() => {
    if (record) {
      resolveFormula(record)
      if (record?.ref_table_detail?.includes(AIR)) {
        getAirData()
      }
      if (record?.ref_table_detail?.includes(HOTEL)) {
        getHotelData(record.hotel?.country)
      }
      if (record?.ref_table_detail?.includes(OTHER)) {
        getOtherSupplier(record?.product_token)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record])

  const getProductType = async (token_product) => {
    try {
      const product = await productServices.getProductByWorkUnitToken(token_product)
      if (product) {
        return {
          product: {
            product_type_id: product?.product_relation?.product_type_id,
          },
        }
      }
      return null
    } catch (error) {}
  }

  const getOtherSupplier = async (product_token) => {
    try {
      setOtherSuppliersLoading(true)
      const product_type = await getProductType(product_token)
      if (product_type) {
        const providers = await getSupplierByProduct(product_type)
        if (providers) {
          setOtherSuppliers(providers)
        }
      }
    } catch (error) {
    } finally {
      setOtherSuppliersLoading(false)
    }
  }

  const resolveFormula = async (record) => {
    if (record && Object.keys(record)?.length > 0) {
      try {
        setFormulasLoading(true)
        if (
          record?.request &&
          Object.keys(record?.request)?.length > 0 &&
          record?.formula?.data &&
          Object.keys(record?.formula?.data)?.length > 0
        ) {
          const { formula, request } = record
          const jsonParse = await mapperHelperService(request)
          updateCheckBox(jsonParse, formula.data, checkedBox, setCheckedBox)
          await waitVariablesFormat(checkedBox, variablesFormat)
        }
        setFormulasLoading(false)
      } catch (error) {}
    }
  }

  const getAirData = () => {
    if (airAirlines?.length === 0) {
      getAirlines()
    }
    if (airDestinations?.length === 0) {
      getDestinations()
    }
    if (airSources?.length === 0) {
      getAirSources()
    }
  }

  const getAirlines = async () => {
    try {
      const allAirlines = await businessRuleServices.getAirlines()
      setAirAirlines(allAirlines)
    } catch (error) {
      setAirAirlines([])
    }
  }

  const getDestinations = async () => {
    try {
      const allDestinations = await businessRuleServices.getDestinations()
      setAirDestinations(allDestinations)
    } catch (error) {
      setAirDestinations([])
    }
  }

  const getAirSources = async () => {
    try {
      const allSources = await businessRuleServices.getAirSources()
      setAirSources(allSources)
    } catch (error) {
      setAirSources([])
    }
  }

  const getHotelData = (defaultCountries = null) => {
    if (hotelSuppliers?.length === 0) {
      getSuppliers()
    }
    if (hotelCountries?.length === 0) {
      getCountries(defaultCountries)
    }
    if (hotelSources?.length === 0) {
      getHotelSources()
    }
  }

  const getSuppliers = async () => {
    try {
      const allSuppliers = await businessRuleServices.getSuppliers()
      const parseIdToString =
        allSuppliers &&
        allSuppliers.forEach((elem) => {
          if (elem?.id) {
            return { ...elem, id: elem.id.toString() }
          }
        })
      setHotelSuppliers(parseIdToString)
    } catch (error) {
      setHotelSuppliers([])
    }
  }

  const getCountries = async (defaultCountries = null) => {
    try {
      const allCountries = await businessRuleServices.getCountries()
      setHotelCountries(allCountries)
      if (defaultCountries) {
        getCities(defaultCountries)
      }
    } catch (error) {
      setHotelCountries([])
    }
  }

  const getCities = async (countries) => {
    if (countries?.length > 0) {
      try {
        const promises = countries.map((country) => businessRuleServices.getCitiesByCountry(country))
        Promise.all(promises).then((allCities) => {
          setHotelCities(allCities.flat())
        })
      } catch (error) {}
    }
  }

  const getHotelSources = async () => {
    try {
      const allSources = await businessRuleServices.getHotelSources()
      setHotelSources(allSources)
    } catch (error) {
      setHotelSources([])
    }
  }

  const getPaymentMethodBank = () => {
    return record?.payment?.card?.[0]?.payment_method?.map((elem) => elem.name).join(', ') || ''
  }

  const getPaymentMethodCard = () => {
    return (
      record?.payment?.card?.[0]?.payment_method
        ?.map((elem) => elem.cards.map((card) => `${elem.name} - ${card.name}`))
        .flat()
        .join(', ') || ''
    )
  }

  console.log('OtherSuppliers', OtherSuppliers)
  return (
    <SimpleShowLayout>
      <CardCommon
        title={translate('ra.entity.business-rule')}
        actions={<CardCrudBottomButtons btnEdit={true} />}
      >
        <Grid container spacing={3}>
          <ShowListElement label={translate('ra.field.id')} value={record?.id} />
          <ShowListElement label={translate('ra.field.name')} value={record?.name} />
          <ShowListElement label={translate('ra.field.description')} value={record?.description} />
        </Grid>

        <Grid container spacing={3}>
          <ShowListElement label={translate('ra.field.work_unit')} value={record?.work_unit_token} />
          <ShowListElement label={translate('ra.field.business_unit')} value={record?.business_unit_token} />
        </Grid>

        <Grid container spacing={3}>
          <ShowListElement label={translate('ra.field.rule_type')} value={record?.rule_type?.long_name} />
          <ShowListElement label={translate('ra.field.parent_businessRule')} value={record?.parent_rule} />
          <ShowListElement label={translate('ra.field.product')} value={record?.product_token} />
        </Grid>

        <Grid container spacing={3}>
          <ShowListElement
            label={translate('ra.field.start_validity_rule')}
            value={record?.rule_date_start}
            type='date'
          />
          <ShowListElement
            label={translate('ra.field.end_validity_rule')}
            value={record?.rule_date_end}
            type='date'
          />
        </Grid>

        <Grid container spacing={3}>
          <ShowListElement label={translate('ra.field.is_active')} value={record?.is_active} type='boolean' />
          <ShowListElement label={translate('ra.field.priority')} value={record?.priority} />
          {record?.rule_type_id === LIQUIDATION && (
            <ShowListElement
              label={translate('ra.field.rule_liquidation_type')}
              value={record?.rule_liquidation_type}
              type={'chip'}
            />
          )}
          {record?.rule_type_id === BILLING && (
            <ShowListElement
              label={translate('ra.field.is_berlin')}
              value={record?.is_berlin}
              type='boolean'
            />
          )}
          {record?.rule_type_id === BILLING && (
            <ShowListElement label={translate('ra.field.base_to_billing')} value={record?.base_to_billing} />
          )}
        </Grid>

        <Grid container spacing={3}>
          <ShowListElement
            label={translate('ra.field.destination_type')}
            value={record?.destination_type?.name}
          />
          {/* {record?.json_billing && <JsonInput defaultValue={record?.json_billing} />} */}
        </Grid>

        {record?.ref_table_detail?.includes(AIR) && (
          <Grid container spacing={3}>
            <ShowListElement
              label={translate('ra.field.airline')}
              value={record?.air?.airline_code}
              type='select'
              multiple
              showAllOption
              data={airAirlines}
              optionText={['long_name']}
              optionValue='short_name'
            />
            <ShowListElement
              label={translate('ra.field.destination')}
              value={record?.air?.destination}
              type='select'
              multiple
              showAllOption
              data={airDestinations}
              optionText={['long_name']}
              optionValue='short_name'
            />
            <ShowListElement
              label={translate('ra.field.source')}
              value={record?.air?.source_relation}
              type='select'
              multiple
              data={airSources}
              optionText={['long_name']}
            />
            <ShowListElement label={translate('ra.field.route')} value={record?.air?.route} type='chip' />
            <ShowListElement
              label={translate('ra.field.is_emd')}
              value={record?.air?.is_emd}
              type='boolean'
            />
          </Grid>
        )}

        {record?.ref_table_detail?.includes(HOTEL) && (
          <Grid container spacing={3}>
            <ShowListElement
              label={translate('ra.field.supplier')}
              value={record?.hotel?.supplier}
              type='select'
              showAllOption
              multiple
              data={hotelSuppliers}
              optionText={['long_name']}
            />
            <ShowListElement
              label={translate('ra.field.country')}
              value={record?.hotel?.country}
              type='select'
              multiple
              data={hotelCountries}
              showAllOption
              optionValue='country_code'
              optionText={['country_name']}
            />
            <ShowListElement
              label={translate('ra.field.city')}
              value={record?.hotel?.city}
              showAllOption
              type='select'
              multiple
              data={hotelCities}
              optionValue='location_id'
              optionText={['location_name']}
            />
            <ShowListElement
              label={translate('ra.field.source')}
              value={record?.hotel?.source_relation}
              type='select'
              multiple
              data={hotelSources}
              optionText={['long_name']}
            />
          </Grid>
        )}

        {record?.ref_table_detail?.includes(OTHER) && (
          <Grid container spacing={3}>
            <ShowListElement
              label={translate('ra.field.supplier')}
              value={record?.other?.supplier}
              type='select'
              showAllOption
              multiple
              data={OtherSuppliers}
              optionText={['name']}
              optionValue='code'
              loading={OtherSuppliersLoading}
            />
          </Grid>
        )}

        {/* {record?.ref_table_detail?.includes(TRAIN) && (
          <Grid container spacing={3}>
            <ShowListElement
              label={translate('ra.field.product_type')}
              value={record?.train?.product_type}
              showAllOption
              multiple
              type='array'
              optionValue={'long_name'}
            />
          </Grid>
        )} */}

        {record?.ref_table_detail?.includes(TRAIN_SEGMENT) && (
          <Grid container spacing={3}>
            <ShowListElement
              label={translate('ra.field.supplier')}
              value={record?.train_segment?.supplier}
              type='select'
              showAllOption
              multiple
              data={[{ code: 'renfe', name: 'Renfe' }]}
              optionText={['name']}
              optionValue='code'
              loading={OtherSuppliersLoading}
            />
          </Grid>
        )}

        {record?.ref_table_detail?.includes(CAR) && (
          <Grid container spacing={3}>
            <ShowListElement
              label={translate('ra.field.renter')}
              value={record?.car?.renter}
              type='select'
              showAllOption
              multiple
              data={renter.data || []}
              optionValue='short_name'
              optionText={['long_name']}
            />

            <ShowListElement
              label={translate('ra.field.destination')}
              value={record?.car?.destination}
              type='array'
              optionValue={'short_name'}
            />
          </Grid>
        )}

        {record?.rule_type_id === PAYMENT_METHOD && (
          <Grid container spacing={3}>
            <ShowListElement
              label={translate('ra.field.payment_type')}
              value={record?.payment?.card?.[0]?.name}
            />
            <ShowListElement label={translate('ra.field.payment_method')} value={getPaymentMethodBank()} />
            <ShowListElement
              label={translate('ra.field.payment_method_by_bank')}
              value={getPaymentMethodCard()}
            />
          </Grid>
        )}

        <CheckboxContext.Provider value={{ checkedBox, setCheckedBox, variablesFormat, setVariablesFormat }}>
          {formulasLoading ? (
            <Skeleton variant='rectangular' width={'10%'} height={50} />
          ) : record?.formula?.structure?.length !== 0 ? (
            <FormulaComponent
              formulas={formulas}
              setFormulas={setFormulas}
              initialFormulas={record?.formula?.structure}
              formula_applied={record?.formula_applied?.formula_applied}
              onlyView={true}
            />
          ) : (
            <Grid container spacing={3}>
              <ShowListElement label={translate('ra.field.formula')} value={isUndefined(undefined)} />
            </Grid>
          )}
        </CheckboxContext.Provider>
      </CardCommon>
    </SimpleShowLayout>
  )
}

export const BusinessRuleShow = (props) => {
  return (
    <Show component={ShowWrapper} {...props} actions={null} className='CrudShowContainer'>
      <ShowContent {...props} />
    </Show>
  )
}
